/* eslint-disable react/no-unescaped-entities */
import React from "react";
import {
  Play,
  Calculator,
  FileText,
  Gauge,
  ArrowRight,
} from "lucide-react";

// 1080×1080 social announcement card.
// Visit /social/announce in a 1080×1080 viewport to render exactly at scale,
// then screenshot for IG / FB / LinkedIn. Designed to read at thumbnail size.
//
// Composition rules:
//   • One dominant headline (left)
//   • One disciplined 2×2 mosaic of feature hints (right)
//   • One URL footer (full-width)
//   • No more than 4 visual elements competing for attention
//
export default function SocialAnnounce() {
  return (
    <div
      style={{
        // EXACT 1080×1080 — Playwright will set viewport to match.
        width: 1080,
        height: 1080,
        background: "#0C2A40",
        color: "#FAFAF7",
        fontFamily: "Montserrat, system-ui, -apple-system, sans-serif",
        position: "relative",
        overflow: "hidden",
        // Top-to-bottom subtle vignette so corners feel cinematic, not flat.
        backgroundImage:
          "radial-gradient(80% 60% at 50% 0%, #14405f 0%, #0C2A40 55%, #081e2e 100%)",
      }}
    >
      {/* Faint hairline grid — engineering-firm motif */}
      <svg
        width="1080"
        height="1080"
        style={{ position: "absolute", inset: 0, opacity: 0.05 }}
      >
        <defs>
          <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#FAFAF7" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="1080" height="1080" fill="url(#grid)" />
      </svg>

      {/* Top bar — wordmark + pill */}
      <div
        style={{
          position: "absolute",
          top: 64,
          left: 72,
          right: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            fontSize: 28,
            fontWeight: 400,
            letterSpacing: "-0.01em",
            color: "#FAFAF7",
          }}
        >
          <span style={{ fontWeight: 600 }}>Hidro</span>
          <span style={{ color: "#2bc0cf" }}>Verse</span>
          <sup style={{ fontSize: 12, marginLeft: 2 }}>™</sup>
        </div>
        <div
          style={{
            border: "1px solid #2bc0cf",
            color: "#2bc0cf",
            fontSize: 13,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            padding: "8px 16px",
            borderRadius: 999,
            fontWeight: 500,
          }}
        >
          Live now
        </div>
      </div>

      {/* Headline — left aligned, top-third */}
      <div
        style={{
          position: "absolute",
          top: 200,
          left: 72,
          right: 72,
        }}
      >
        <div
          style={{
            color: "#2bc0cf",
            fontSize: 18,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            marginBottom: 20,
            fontWeight: 500,
          }}
        >
          A new website.
        </div>
        <h1
          style={{
            fontSize: 96,
            lineHeight: 0.96,
            letterSpacing: "-0.025em",
            fontWeight: 300,
            margin: 0,
            color: "#FAFAF7",
          }}
        >
          The new
          <br />
          <span style={{ fontWeight: 500 }}>HidroVerse.</span>
        </h1>
        <p
          style={{
            marginTop: 24,
            fontSize: 22,
            lineHeight: 1.4,
            color: "rgba(250,250,247,0.72)",
            maxWidth: 720,
            fontWeight: 300,
          }}
        >
          Engineered water systems — for lodges, estates, mines and municipalities.
        </p>
      </div>

      {/* 2×2 feature mosaic — bottom half */}
      <div
        style={{
          position: "absolute",
          bottom: 188,
          left: 72,
          right: 72,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "1fr 1fr",
          gap: 18,
          height: 360,
        }}
      >
        {/* Tools */}
        <FeatureCard
          icon={<Calculator size={28} strokeWidth={1.6} />}
          eyebrow="Tools"
          title="16 engineering calculators."
          meta="Pump sizing · RO recovery · LSI · Solar PV"
        />
        {/* Videos */}
        <FeatureCard
          icon={<Play size={28} strokeWidth={1.6} fill="#2bc0cf" />}
          eyebrow="Videos"
          title="Walk-arounds. Field installs."
          meta="11 site films · published weekly"
        />
        {/* Insights */}
        <FeatureCard
          icon={<FileText size={28} strokeWidth={1.6} />}
          eyebrow="Insights"
          title="Engineering essays + PDFs."
          meta="SANS 241 · Borehole · MBBR · Compliance"
        />
        {/* Portal */}
        <FeatureCard
          icon={<Gauge size={28} strokeWidth={1.6} />}
          eyebrow="Customer Portal"
          title="Your sites, on tap."
          meta="Quotes · service tickets · billing"
        />
      </div>

      {/* Bottom URL strip */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "40px 72px",
          borderTop: "1px solid rgba(43,192,207,0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background:
            "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.18) 100%)",
        }}
      >
        <div
          style={{
            fontSize: 14,
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            color: "rgba(250,250,247,0.55)",
            fontWeight: 500,
          }}
        >
          Universal Water Systems
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            fontSize: 34,
            fontWeight: 500,
            color: "#FAFAF7",
            letterSpacing: "-0.01em",
          }}
        >
          hidroverse.co.za
          <span
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              background: "#2bc0cf",
              color: "#0C2A40",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ArrowRight size={28} strokeWidth={2.4} />
          </span>
        </div>
      </div>
    </div>
  );
}

function FeatureCard({ icon, eyebrow, title, meta }) {
  return (
    <div
      style={{
        background: "rgba(250,250,247,0.04)",
        border: "1px solid rgba(43,192,207,0.18)",
        borderRadius: 16,
        padding: "22px 24px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        backdropFilter: "blur(8px)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          color: "#2bc0cf",
        }}
      >
        {icon}
        <span
          style={{
            fontSize: 12,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            fontWeight: 500,
          }}
        >
          {eyebrow}
        </span>
      </div>
      <div
        style={{
          fontSize: 24,
          lineHeight: 1.18,
          color: "#FAFAF7",
          fontWeight: 500,
          letterSpacing: "-0.01em",
          marginTop: 10,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 13,
          color: "rgba(250,250,247,0.55)",
          fontWeight: 400,
          marginTop: 8,
          letterSpacing: "0.01em",
        }}
      >
        {meta}
      </div>
    </div>
  );
}
